// Utility function to transform legacy payload format to new API format
// CRITICAL: currentUserId must be the correct person's UUID, not an invitation ID
export const transformPayload = (currentPayload, currentUserId, parentInfo = null) => {
  // Validate that currentUserId is provided and looks like a UUID
  if (!currentUserId) {
    throw new Error('currentUserId is required for relationship creation');
  }
  
  if (currentUserId.length < 32) {
    console.warn('⚠️ WARNING: currentUserId appears to be too short, verify it is a person UUID not invitation ID:', currentUserId);
  }
  const human = currentPayload.human;
  const relationshipType = currentPayload.relationship_type.name.toLowerCase();

  // Base payload structure
  const basePayload = {
    relationship_type: relationshipType,
    new_person_data: {
      first_name: human.first_name,
      last_name: human.last_name,
      gender: human.gender || null,
      living: human.living !== undefined ? human.living : true,
      date_of_birth: human.date_of_birth || null,
      privacy_level: "family",
      // Optional fields
      ...(human.email && { email: human.email }),
      ...(human.phone && { phone: human.phone }),
      ...(human.middle_name && { middle_name: human.middle_name }),
      ...(human.city_of_birth && { city_of_birth: human.city_of_birth }),
      ...(human.country_of_birth && { country_of_birth: human.country_of_birth }),
      // Minor support
      ...(human.is_minor !== undefined && { is_minor: human.is_minor }),
      // Tree-only support (deceased ancestors, non-tech-savvy family)
      ...(human.is_tree_only && { is_tree_only: true }),
      ...(human.availability_status && { availability_status: human.availability_status }),
      ...(human.death_year && { death_year: human.death_year })
    }
  };

  // Handle dual parent scenarios for children
  if (parentInfo && (relationshipType === 'son' || relationshipType === 'daughter' || relationshipType === 'child')) {
    
    return {
      ...basePayload,
      parent1_id: parentInfo.parent1.id,
      ...(parentInfo.parent2 && { parent2_id: parentInfo.parent2.id })
    };
  }

  // Default single parent/person relationship
  return {
    ...basePayload,
    existing_person_id: currentUserId
  };
};

// Alternative: Direct payload builder for new format
// CRITICAL: Ensures correct person IDs are used for relationship creation
export const buildNewPayload = (personData, relationshipType, currentUserId) => {
  // Validate inputs
  if (!currentUserId) {
    throw new Error('currentUserId is required for relationship creation');
  }
  
  if (currentUserId.length < 32) {
    console.warn('⚠️ WARNING: currentUserId appears to be too short, verify it is a person UUID:', currentUserId);
  }
  
  return {
    existing_person_id: currentUserId,
    relationship_type: relationshipType.toLowerCase(),
    new_person_data: {
      first_name: personData.first_name,
      last_name: personData.last_name,
      gender: personData.gender || null,
      living: personData.living !== undefined ? personData.living : true,
      date_of_birth: personData.date_of_birth || null,
      privacy_level: "family",
      // Optional fields
      ...(personData.email && { email: personData.email }),
      ...(personData.phone && { phone: personData.phone }),
      ...(personData.middle_name && { middle_name: personData.middle_name }),
      ...(personData.city_of_birth && { city_of_birth: personData.city_of_birth }),
      ...(personData.country_of_birth && { country_of_birth: personData.country_of_birth }),
      // Minor support
      ...(personData.is_minor !== undefined && { is_minor: personData.is_minor }),
      // Tree-only support (deceased ancestors, non-tech-savvy family)
      ...(personData.is_tree_only && { is_tree_only: true }),
      ...(personData.availability_status && { availability_status: personData.availability_status }),
      ...(personData.death_year && { death_year: personData.death_year })
    }
  };
};

// Helper function for creating spouse relationships with Anil Patel example
export const createSpouseRelationship = async (personId, newPersonData, authToken) => {
  // CRITICAL: Use the correct person's ID
  // For Anil Patel as Sonali's spouse, use Sonali's ID:
  const payload = buildNewPayload(newPersonData, 'spouse', personId);

  const response = await fetch('/api/genealogy/people/create_with_relationship/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Token ${authToken}`
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    throw new Error(`Failed to create spouse relationship: ${response.statusText}`);
  }

  return response.json();
};